<template>
    <div class="analises-melhorias-container">
        <div class="page-header">
            <div class="d-flex align-items-center">
                <button class="btn-back" @click="$router.push('/')">
                    <i class="fas fa-arrow-left"></i>
                </button>
                <div class="header-titles ml-3">
                    <div class="header-icon-title">
                        <h1 class="page-title">Métricas Operacionais</h1>
                    </div>
                     <p class="page-subtitle">Operação, produtividade e conformidade técnica</p>
                </div>
            </div>
        </div>
    <main class="main-content">
    <div v-if="summaryError" class="error-banner">
      <span>{{ summaryError }}</span>
      <button class="btn-fechar-erro" @click="summaryError = ''">&times;</button>
    </div>
    <div class="cards-grid">
    <Indicador
        v-for="(card, index) in indicatorCards"
        :key="card.label || index"
        :label="card.label"
        :value="card.value"
        :unit="card.unit"
        :variation="card.variation"
        :show-trend="card.showTrend"
        :caption="card.caption"
    />
    </div>
           <AnalisesChart
             :selected-year="selectedYear"
             @period-change="onChartPeriodChange"
           />
        <div class="bottom-grid-section">
        <div class="historico-column">
          <historicoRecente
            :equipments="equipments"
            :selected-equipment-id="selectedEquipmentId"
            @update:selectedEquipmentId="selectedEquipmentId = $event"
          />
        </div>
        <div class="tempo-uso-column">
          <MachineUtilization
            :maquina-id="selectedEquipmentId"
            :data-selecionada="usageDate"
          />
        </div>
      </div>
    </main>

    </div>
</template>

<script>
import Indicador from '@/components/views/Cards/subcardsMelhorias/indicador.vue'
import AnalisesChart from '@/components/views/Cards/subcardsMelhorias/analises-chart.vue'
import historicoRecente from '@/components/views/Cards/subcardsMelhorias/historicoRecente.vue'
import MachineUtilization from '@/components/views/Cards/subcardsMelhorias/MachineUtilization.vue'
import { api } from '@/services'
import { DB_ROUTES } from '@/constants'

const currentYear = new Date().getFullYear()

function formatMonthAnalyses (count) {
  const n = Number(count) || 0
  return n === 1 ? '1 no mês atual' : `${n} no mês atual`
}

function formatDurationParts (seconds) {
  const total = Math.abs(Math.round(Number(seconds) || 0))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  if (minutes === 0) return `${secs}s`
  if (secs === 0) return `${minutes}min`
  return `${minutes}min ${secs}s`
}

function formatDurationTrend (seconds) {
  const n = Math.round(Number(seconds) || 0)
  const sign = n > 0 ? '+' : n < 0 ? '-' : ''
  return `${sign}${formatDurationParts(n)} vs mês anterior`
}

function formatDurationReference (months) {
  const list = (Array.isArray(months) ? months : []).filter(item => item && item.label)
  if (!list.length) return ''

  const years = Array.from(new Set(list.map(item => item.year)))
  const singleYear = years.length === 1
  const names = singleYear
    ? list.map(item => item.label)
    : list.map(item => `${item.label} de ${item.year}`)
  const yearSuffix = singleYear ? ` de ${years[0]}` : ''

  if (singleYear && list.length === 12) {
    return `Referente a todos os meses${yearSuffix}`
  }
  if (names.length === 1) {
    return `Referente ao mês de ${names[0]}${yearSuffix}`
  }

  const last = names[names.length - 1]
  const head = names.slice(0, -1).join(', ')
  return `Referente aos meses de ${head} e ${last}${yearSuffix}`
}

function formatAverageDuration (seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  if (minutes === 0) {
    return { value: secs, unit: 's' }
  }
  return { value: minutes, unit: `min ${secs}s` }
}

export default {
  components: {
    Indicador,
    AnalisesChart,
    historicoRecente,
    MachineUtilization
  },

  data () {
    return {
      selectedYear: currentYear,
      selectedEquipmentId: null,
      equipments: [],
      summaryError: '',
      chartPeriodStart: '',
      chartPeriodEnd: '',
      indicatorCards: [
        { label: 'FUNCIONÁRIOS ATIVOS', value: '—', unit: '', variation: 'Desde o cadastro da empresa', showTrend: false },
        { label: 'TOTAL DE ANÁLISES', value: '—', unit: '', variation: '—', showTrend: false },
        { label: 'DURAÇÃO DAS ANÁLISES', value: '—', unit: '', variation: '—', showTrend: true, caption: '' }
      ]
    }
  },

  computed: {
    usageDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  },

  async created () {
    await this.loadEquipments()
    await this.loadSummary()
  },

  methods: {
    yearRange () {
      return {
        startDate: `${this.selectedYear}-01-01`,
        endDate: `${this.selectedYear}-12-31`
      }
    },

    setDurationFromChart (averageSeconds, trendSeconds, selectedMonths) {
      const duration = formatAverageDuration(averageSeconds)
      const next = this.indicatorCards.slice()
      const previous = next[2] || { variation: '—', caption: '' }
      const caption = selectedMonths
        ? formatDurationReference(selectedMonths)
        : (previous.caption || '')
      next[2] = {
        label: 'DURAÇÃO DAS ANÁLISES',
        value: duration.value,
        unit: duration.unit,
        variation: trendSeconds == null ? previous.variation : formatDurationTrend(trendSeconds),
        showTrend: true,
        caption
      }
      this.indicatorCards = next
    },

    async loadEquipments () {
      try {
        const response = await api.get('Equipments/with-names')
        const list = Array.isArray(response.data) ? response.data : []
        this.equipments = list.map(item => ({
          id: item.id ?? item.Id,
          identification: item.equipmentIdentification || item.EquipmentIdentification || `Equipamento ${item.id ?? item.Id}`
        })).filter(item => item.id != null)

        if (!this.selectedEquipmentId && this.equipments.length > 0) {
          this.selectedEquipmentId = this.equipments[0].id
        }
      } catch (error) {
        console.error('Erro ao carregar equipamentos:', error)
        this.equipments = []
      }
    },

    async loadSummary () {
      this.summaryError = ''
      try {
        const { startDate, endDate } = this.yearRange()
        const response = await api.get(DB_ROUTES.GET_ANALYSIS_DASHBOARD_SUMMARY({
          startDate,
          endDate
        }))
        const summary = response.data || {}
        const durationCard = this.indicatorCards[2] || {
          label: 'DURAÇÃO DAS ANÁLISES',
          value: '—',
          unit: '',
          variation: '—',
          showTrend: true,
          caption: ''
        }

        this.indicatorCards = [
          {
            label: 'FUNCIONÁRIOS ATIVOS',
            value: summary.activeEmployees ?? 0,
            unit: '',
            variation: 'Desde o cadastro da empresa',
            showTrend: false
          },
          {
            label: 'TOTAL DE ANÁLISES',
            value: summary.totalAnalyses ?? 0,
            unit: '',
            variation: formatMonthAnalyses(summary.analysesThisMonth),
            showTrend: false
          },
          durationCard
        ]
      } catch (error) {
        console.error('Erro ao carregar indicadores:', error)
        this.summaryError = error.response?.data?.message || 'Não foi possível carregar os indicadores.'
      }
    },

    async onChartPeriodChange ({ startDate, endDate, averageDurationSeconds, selectedMonths }) {
      if (!startDate || !endDate) return

      if (typeof averageDurationSeconds === 'number') {
        this.setDurationFromChart(averageDurationSeconds, null, selectedMonths)
      }

      if (this.chartPeriodStart === startDate && this.chartPeriodEnd === endDate) return

      this.chartPeriodStart = startDate
      this.chartPeriodEnd = endDate

      try {
        const response = await api.get(DB_ROUTES.GET_ANALYSIS_DASHBOARD_SUMMARY({
          startDate,
          endDate
        }))
        const summary = response.data || {}
        this.setDurationFromChart(
          typeof averageDurationSeconds === 'number'
            ? averageDurationSeconds
            : summary.averageDurationSeconds,
          summary.averageDurationTrendSeconds,
          selectedMonths
        )
      } catch (error) {
        console.error('Erro ao carregar duração média:', error)
      }
    }
  }
}
</script>

<style scoped>
.analises-melhorias-container {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Cards */

.cards-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr) !important;
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;
}

.d-flex {
  display: flex;
  align-items: center;
  width: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 30px;
}

.btn-back {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border: none;
  color: white;
  width: 45px;
  height: 45px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
  flex-shrink: 0;
}

.btn-back:hover {
  background: linear-gradient(135deg, #FF7A29 0%, #FF6810 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
}

.header-icon-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #FFF0E4;
  color: #FF7A29;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0;
}

.page-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
  margin: 4px 0 0 0;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.error-banner {
  background-color: #FEE2E2;
  color: #991B1B;
  padding: 10px 14px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.btn-fechar-erro {
  background: none;
  border: none;
  color: #991B1B;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}

/* Mobile */
@media (max-width: 768px) {
  .page-title {
    font-size: 1.5rem;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .cards-grid {
    grid-template-columns: 1fr !important;
  }

  .bottom-grid-section {
    flex-direction: column;
  }
}

.bottom-grid-section {
  display: flex;
  gap: 24px;
  margin-top: 24px;
  align-items: stretch;
}

.historico-column {
  flex: 2;
  min-width: 0;
  overflow: visible;
}

.tempo-uso-column {
  flex: 1;
  min-width: 0;
}

.placeholder-tempo-uso {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
  font-family: 'Inter', sans-serif;
  height: 100%;
}

.placeholder-tempo-uso h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.placeholder-tempo-uso p {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #888888;
}
</style>
